import { EditMode } from '../configurator/model/edit-mode';
import { debounce, percentToDegrees } from '../configurator/helpers';
import { Translator } from '../configurator/translator';
import { HeightsRenderer } from './heights-renderer';
import { HeightsEditState } from './heights-edit-state';
import { HeightsUI } from './heights-ui';

export default class HeightsConfigurator {
  constructor(options = {}) {
    ({
      canvasSelector: this.canvasSelector,
      inputSelector: this.inputSelector,
      toolbarSelector: this.toolbarSelector,
      sidebarSelector: this.sidebarSelector,
      heightPopupSelector: this.heightPopupSelector,
      slopePopupSelector: this.slopePopupSelector,
      overlaySelector: this.overlaySelector,
      helpSelector: this.helpSelector,
      onInited: this.onInited,
      translations: this.translations,
      initialMinHeight: this.initialMinHeight,
      minHeightLimit: this.minHeightLimit,
      initialSlope: this.initialSlope,
      initialSlopeMode: this.initialSlopeMode,
      defaultTool: this.defaultTool,
      highlightColor: this.highlightColor,
    } = options);

    this.lastUsedHeight = this.initialMinHeight;

    this.editState = this.createEditState(options.areas);
    this.translator = new Translator({ translations: options.translations });
    this.viewInitialized = false;

    this.renderer = new HeightsRenderer({
      canvasSelector: this.canvasSelector,
      editState: this.editState,
      editMode: EditMode.POINTER,
      translator: this.translator,
      onAfterInit: this.onAfterInit.bind(this),
      onPointAdded: this.pointAdded.bind(this),
      onPointClicked: this.pointClicked.bind(this),
      onGroupSelection: this.groupSelected.bind(this),
      onSlopeStartSelected: this.slopeStartSelected.bind(this),
      onAllSlopesDefined: this.allSlopesDefined.bind(this),
      onPointRemoved: this.pointRemoved.bind(this),
      onSlopeCalculation: this.slopeCalculated.bind(this),
      highlightColor: this.highlightColor,
    });

    this.ui = new HeightsUI({
      toolbarSelector: this.toolbarSelector,
      canvasSelector: this.canvasSelector,
      sidebarSelector: this.sidebarSelector,
      heightPopupSelector: this.heightPopupSelector,
      slopePopupSelector: this.slopePopupSelector,
      overlaySelector: this.overlaySelector,
      helpSelector: this.helpSelector,
      editMode: this.editMode,
      onZoomIn: this.zoomIn.bind(this),
      onZoomOut: this.zoomOut.bind(this),
      initialSlopeMode: this.initialSlopeMode,
      onSlopeToolSelected: this.slopeToolSelected.bind(this),
      onAddPointsSelected: this.addPointsToolSelected.bind(this),
      onEditPointsSelected: this.editPointsToolSelected.bind(this),
      onMinHeightSelected: this.minHeightToolSelected.bind(this),
      onHeightPopupConfirmed: this.heightPopupConfirmed.bind(this),
      onHeightPopupRejected: this.heightPopupRejected.bind(this),
      onSlopePopupConfirmed: this.slopePopupConfirmed.bind(this),
      onSlopePopupRejected: this.slopePopupRejected.bind(this),
      onModeChanged: this.changeMode.bind(this),
      translator: this.translator,
      additionalOptions: this.additionalOptions,
    });

    this.handleOnPageResize();
    this._updateHeightRanges();
  }

  createEditState(rawareas) {
    return new HeightsEditState({
      rawAreas: rawareas,
      initialMinHeight: this.initialMinHeight,
      initialSlope: this.initialSlopeMode === 'percent' ? percentToDegrees(this.initialSlope) : this.initialSlope,
    });
  }

  zoomIn() {
    this.renderer.zoomIn();
  }

  zoomOut() {
    this.renderer.zoomOut();
  }

  slopeToolSelected() {
    this.renderer.slopeToolSelected();
  }

  slopeStartSelected(point, coordinates) {
    this.activePoint = point;
    this.ui.openSlopePopup(point, {
      defaultValue: point.parent.slope,
      coordinates,
    });
  }

  addPointsToolSelected() {
    this.renderer.addPointsToolSelected();
  }

  editPointsToolSelected() {
    this.renderer.editPointsToolSelected();
  }

  minHeightToolSelected() {
    this.renderer.minHeightToolSelected();
  }

  groupSelected(heightPoints, coordinates) {
    this.activePoint = null;
    this.activePoints = heightPoints;
    this.ui.openHeightPopup(null, {
      minHeight: this.editState.minHeight,
      minHeightLimit: this.minHeightLimit,
      defaultValue: this.lastUsedHeight,
      coordinates,
    });
  }

  pointAdded(point, coordinates) {
    this.activePoint = point;
    this.ui.openHeightPopup(point, {
      minHeight: this.editState.minHeight,
      minHeightLimit: this.minHeightLimit,
      defaultValue: this.lastUsedHeight,
      coordinates,
    });
  }

  pointClicked(point, coordinates) {
    this.activePoint = point;
    this.ui.openHeightPopup(point, {
      minHeight: this.editState.minHeight,
      minHeightLimit: this.minHeightLimit,
      coordinates,
    });
  }

  onAfterInit() {
    this.viewInitialized = true;
    if (this.defaultTool === 'slope') {
      this.ui.selectSlopeTool();
    } else {
      this.ui.selectAddTool();
    }
  }

  handleOnPageResize() {
    window.addEventListener(
      'resize',
      debounce(() => {
        if (!this.viewInitialized) {
          return;
        }
        this.renderer.fitPlanToView();
        this.renderer.redraw();
      }, 100)
    );
  }

  heightPopupConfirmed(value) {
    if (this.activePoint) {
      this.activePoint.height = value;
      this.activePoint.finished = true;
    } else if (this.activePoints?.length) {
      this.activePoints.forEach(point => {
        point.height = value;
        point.finished = true;
        point.highlighted = false;
      });
    }
    this.lastUsedHeight = value;
    this.renderer.redraw();
    this._updateHeightRanges();
  }

  heightPopupRejected() {
    this.editState.clearPointHighlights();
    if (this.activePoint?.finished === false) {
      this.editState.removePoint(this.activePoint);
    }
    this.activePoint = undefined;
    this.activePoints = undefined;
    this.renderer.redraw();
  }

  slopePopupConfirmed(value, slopeUserSelection) {
    if (this.activePoint) {
      const area = this.activePoint.parent;
      area.slope = value;
      area.slopeUserSelection = slopeUserSelection;
      area.slopeStartPoint = this.activePoint;
      this.renderer.slopeInteracted();
    }
    this.activePoint = null;
    this._updateHeightRanges();
  }

  pointRemoved() {
    this._updateHeightRanges();
  }

  slopeCalculated() {
    console.log('here');
    this._updateHeightRanges();
  }

  slopePopupRejected() {
    if (this.activePoint) {
      const area = this.activePoint.parent;
      area.slopeStartPoint = null;
      this.renderer.slopeInteracted();
    }
    this.activePoint = null;
  }

  allSlopesDefined() {
    this._updateHeightRanges();
    this.ui.selectAddTool();
  }

  changeMode(mode) {
    this.editMode = mode;
    this.renderer.switchEditMode(this.editMode);
  }

  get isValid() {
    return this.editState.isValid;
  }

  submit() {
    if (this.isValid) {
      if (
        !this.editState.hasSomeHeightpointChanged &&
        !window.confirm(tp_trans.trans('proceed_with_no_interaction', 'proceed_with_no_interaction'))
      ) {
        return;
      }

      if (
        this.editState.isLowestPointHigherThenInitialMinHeight &&
        !window.confirm(
          tp_trans.trans('proceed_with_points_higher_then_initial', 'proceed_with_points_higher_then_initial', {
            height: this.initialMinHeight,
          })
        )
      ) {
        return;
      }

      return this.editState.backendObjects;
    } else {
      alert(tp_trans.trans('invalid_height_state', 'invalid_height_state'));
    }
  }

  _updateHeightRanges() {
    this.ui.updateHeightRange(this.editState.heightRange);
  }
}
